// Compass Phase 3.6 · 2026-05-24
//
// Compact super-admin-only progress badge that lives in the top-right
// of every internal page. Shows live populated/partial/shell counts +
// % progress, and links to the full Compass Index for drill-down.
//
// Self-gates on `useAuth().user.is_super_admin` so collaborators (once
// invited) inherit access without a separate prop wire-up. Renders
// nothing for tenants — three-wall guardrail consistent with the rest
// of the internal-only surface.
import { useEffect, useState } from "react";
import { Compass, ChevronDown, ExternalLink, FileText } from "lucide-react";

import { api } from "../lib/api";
import { useAuth } from "../App";

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes — cheap endpoint but no need to hammer

function SectionRow({ label, stats }) {
  if (!stats?.total) return null;
  const pct = Math.round((stats.populated / stats.total) * 100);
  return (
    <div className="flex items-center gap-2 text-[11px] font-mono" data-testid={`compass-progress-section-${label}`}>
      <span className="text-[var(--app-text-muted)] flex-1 truncate">{label}</span>
      <span className="text-emerald-300 tabular-nums w-7 text-right">{stats.populated}</span>
      <span className="text-zinc-500">/</span>
      <span className="text-[var(--app-text-muted)] tabular-nums w-7 text-right">{stats.total}</span>
      <span className="text-violet-300 tabular-nums w-9 text-right">{pct}%</span>
    </div>
  );
}

export function CompassProgressBadge() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let cancelled = false;
    const load = () => {
      api.get("/dev-library/compass/progress")
        .then((r) => { if (!cancelled) { setData(r.data); setError(false); } })
        .catch(() => { if (!cancelled) setError(true); });
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [isSuperAdmin]);

  if (!isSuperAdmin || error || !data) return null;

  // Pretty section names (drops the "0X_" prefix).
  const sections = Object.entries(data.by_section || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, stats]) => [key.replace(/^\d+_/, ""), stats]);

  return (
    <div
      className="inline-block"
      data-testid="compass-progress-badge"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-mono border shadow-sm transition-all ${
          open
            ? "bg-violet-600 border-violet-500 text-white"
            : "bg-[var(--app-card)] border-[var(--app-border)] text-[var(--app-text)] hover:border-violet-500/60"
        }`}
        data-testid="compass-progress-toggle"
        title="Compass documentation backlog (super-admin only)"
      >
        <Compass className={`w-3 h-3 ${open ? "" : "text-violet-500 dark:text-violet-300"}`} />
        <span className="tabular-nums font-semibold">
          {data.populated}
          <span className="text-[var(--app-text-muted)] mx-0.5">/</span>
          {data.total}
        </span>
        <span className={`tabular-nums ${open ? "text-violet-100" : "text-violet-500 dark:text-violet-300"}`}>
          {data.pct_populated}%
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-lg border border-[var(--app-border)] bg-[var(--app-card)] shadow-2xl p-3 space-y-2 z-50"
          data-testid="compass-progress-panel"
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase text-violet-300 tracking-wider">Compass · backlog</div>
              <div className="text-sm font-semibold text-[var(--app-text)]">{data.populated} of {data.total} populated</div>
            </div>
            <a
              href="/api/dev-library/compass-index.html"
              target="_blank"
              rel="noreferrer"
              className="text-[10px] px-2 py-1 rounded bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/40 text-violet-200 inline-flex items-center gap-1"
              data-testid="compass-progress-open-index"
            >
              Open INDEX <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </div>

          {/* Bar */}
          <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-emerald-500"
              style={{ width: `${data.pct_populated}%` }}
              data-testid="compass-progress-bar"
            />
          </div>

          {/* Headline tally */}
          <div className="flex items-center gap-3 text-[11px] font-mono">
            <span className="text-emerald-300">🟢 {data.populated} populated</span>
            <span className="text-amber-300">🟡 {data.partial} partial</span>
            {data.shell > 0 && <span className="text-red-300">🔴 {data.shell} shell</span>}
          </div>

          <hr className="border-[var(--app-border)]" />

          {/* Per-section breakdown */}
          <div className="space-y-1">
            <div className="text-[10px] font-mono uppercase text-[var(--app-text-muted)] tracking-wider">by section</div>
            {sections.map(([label, stats]) => (
              <SectionRow key={label} label={label} stats={stats} />
            ))}
          </div>

          {/* Compass Phase 3.6.1 — Pick-a-partial quick action.
              Top 5 oldest partials by `last_reviewed` deep-link into the
              Dev Library viewer with `?doc=` URL sync. Closes the loop
              from "I see the backlog" to "I'm draining it" in two clicks. */}
          {(data.oldest_partials || []).length > 0 && (
            <>
              <hr className="border-[var(--app-border)]" />
              <div className="space-y-1">
                <div className="text-[10px] font-mono uppercase text-[var(--app-text-muted)] tracking-wider">
                  pick a partial to populate
                </div>
                {data.oldest_partials.map((doc) => {
                  const short = doc.section || doc.filename.replace(/^COMPASS\//, "");
                  return (
                    <a
                      key={doc.filename}
                      href={`/platform/dev-library?doc=${encodeURIComponent(doc.filename)}`}
                      onClick={() => setOpen(false)}
                      className="group flex items-center gap-2 px-1.5 py-1 rounded text-[11px] font-mono hover:bg-violet-500/10 border border-transparent hover:border-violet-500/30"
                      data-testid={`compass-progress-partial-${doc.filename.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
                      title={`${doc.filename} · last reviewed ${doc.last_reviewed || "—"}`}
                    >
                      <FileText className="w-3 h-3 text-amber-300 flex-shrink-0" />
                      <span className="text-[var(--app-text)] truncate flex-1 group-hover:text-violet-200">
                        {doc.title}
                      </span>
                      <span className="text-[9px] text-zinc-500 tabular-nums">
                        §{short}
                      </span>
                    </a>
                  );
                })}
              </div>
            </>
          )}

          <div className="text-[9px] font-mono text-zinc-600 pt-1">
            refresh every 5 min · live from frontmatter
          </div>
        </div>
      )}
    </div>
  );
}
